import { decodeQueryValue, encodeQueryValue } from "./query-state";
import { useSyncExternalStore, type AnchorHTMLAttributes } from "react";

const navigationEvent = "sat:navigate";
const snapshot = () => window.location.pathname + window.location.search + window.location.hash;
function subscribe(listener: () => void) {
  window.addEventListener(navigationEvent, listener);
  window.addEventListener("popstate", listener);
  window.addEventListener("hashchange", listener);
  return () => {
    window.removeEventListener(navigationEvent, listener);
    window.removeEventListener("popstate", listener);
    window.removeEventListener("hashchange", listener);
  };
}
export function useLocation() {
  const location = useSyncExternalStore(subscribe, snapshot);
  return new URL(location, window.location.origin);
}

export function navigate(href: string, replace = false) {
  const next = new URL(href, window.location.href);
  // Local role switches use different identities: reload to discard account state.
  const area = (path: string) => path.startsWith("/student/") ? "student" : "admin";
  if (next.origin !== window.location.origin || area(next.pathname) !== area(window.location.pathname)) {
    if (replace) window.location.replace(next.href);
    else window.location.assign(next.href);
    return;
  }
  if (next.href === window.location.href) return;
  window.history[replace ? "replaceState" : "pushState"](null, "", next);
  window.dispatchEvent(new Event(navigationEvent));
}

export function Link({ href, onClick, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  return <a {...props} href={href} onClick={(event) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || props.download !== undefined || (props.target && props.target !== "_self")) return;
    event.preventDefault();
    navigate(href);
  }} />;
}

/** Keep list state in the URL so opening and closing a result preserves it. */
export function useQueryState<T>(key: string, initial: T): [T, (next: T | ((previous: T) => T)) => void] {
  const location = useLocation();
  const value = decodeQueryValue(location.searchParams.get(key), initial);
  return [value, (next) => {
    const target = new URL(window.location.href);
    const updated = typeof next === "function" ? (next as (previous: T) => T)(value) : next;
    target.searchParams.set(key, encodeQueryValue(updated));
    navigate(target.pathname + target.search + target.hash, true);
  }];
}
